import React from 'react';
import { Outlet } from 'react-router-dom';
import { useSessionManager } from '../hooks/useSessionManager';
import { 
  Box, 
  AppBar, 
  Toolbar, 
  IconButton, 
  Typography, 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  ListItemButton,
  Divider,
  Avatar,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Menu as MenuIcon,
  Inventory as InventoryIcon,
  ShoppingCart as ShoppingCartIcon,
  Store as StoreIcon,
  LocationOn as LocationIcon,
  Assessment as ReportsIcon,
  AccountBalance as CashDrawerIcon,
  QrCodeScanner as BarcodeIcon,
  CloudUpload as MigrationIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  People as UsersIcon,
  TrendingUp as CommissionsIcon,
  History as AuditIcon,
  Receipt as SalesHistoryIcon,
  LocalOffer as DiscountIcon,
  SwapHoriz as TransferIcon,
  PlaylistAdd as BulkEditIcon,
  Search as TrackingIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { logout } from '../store/slices/authSlice';
import { toggleSidebar } from '../store/slices/uiSlice';
import { useLogoutMutation } from '../services/auth.service';

const drawerWidth = 240;

interface NavigationMenuItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  adminOnly?: boolean;
  managerOnly?: boolean;
  cashierAllowed?: boolean; // New flag for cashier access
}

const menuItems: NavigationMenuItem[] = [
  { text: 'Productos', icon: <InventoryIcon />, path: '/products', cashierAllowed: true },
  { text: 'Inventario', icon: <StoreIcon />, path: '/inventory', cashierAllowed: true },
  { text: 'Ajuste Masivo', icon: <BulkEditIcon />, path: '/inventory/bulk-adjustment', cashierAllowed: true },
  { text: 'Rastreo', icon: <TrackingIcon />, path: '/rastreo', cashierAllowed: true },
  { text: 'Ventas', icon: <ShoppingCartIcon />, path: '/sales', cashierAllowed: true },
  { text: 'Transferencias', icon: <TransferIcon />, path: '/transfers', cashierAllowed: true },
  { text: 'Historial de Ventas', icon: <SalesHistoryIcon />, path: '/sales-history', adminOnly: true },
  { text: 'Caja Registradora', icon: <CashDrawerIcon />, path: '/cash-drawer', cashierAllowed: true },
  { text: 'Códigos de Barras', icon: <BarcodeIcon />, path: '/barcodes', cashierAllowed: true },
  { text: 'Usuarios', icon: <UsersIcon />, path: '/users', adminOnly: true },
  // Discount configuration disabled for security
  // { text: 'Descuentos', icon: <DiscountIcon />, path: '/discounts', adminOnly: true },
  { text: 'Comisiones', icon: <CommissionsIcon />, path: '/commissions', adminOnly: true },
  { text: 'Migración Excel', icon: <MigrationIcon />, path: '/migration', adminOnly: true },
  { text: 'Reportes', icon: <ReportsIcon />, path: '/reports', adminOnly: true },
  { text: 'Logs de Auditoría', icon: <AuditIcon />, path: '/audit-logs', adminOnly: true },
  { text: 'Configuraciones', icon: <SettingsIcon />, path: '/settings', adminOnly: true },
  { text: 'Ubicaciones', icon: <LocationIcon />, path: '/locations', adminOnly: true },
];

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  
  const { user } = useSelector((state: RootState) => state.auth);
  
  // Initialize session manager to handle automatic logout on inactivity
  useSessionManager();
  const { sidebarOpen } = useSelector((state: RootState) => state.ui);
  
  const [logoutMutation] = useLogoutMutation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleLogout = async () => {
    try {
      await logoutMutation().unwrap();
    } catch (error) {
      // Ignorar errores del logout en el servidor
    } finally {
      dispatch(logout());
      navigate('/login');
    }
  };

  const handleDrawerToggle = () => {
    dispatch(toggleSidebar());
  };

  const filteredMenuItems = menuItems.filter(item => {
    // Admin can access everything
    if (user?.role === 'ADMIN') {
      return true;
    }
    
    // Both Manager and Cashier can only access cashierAllowed items (the 4 basic pages)
    if (user?.role === 'MANAGER' || user?.role === 'CASHIER') {
      return item.cashierAllowed;
    }
    
    return false;
  });

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div" sx={{ color: 'primary.main' }}>
          Plásticos Juticalpa
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {filteredMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => navigate(item.path)}
            >
              <ListItemIcon sx={{ color: location.pathname === item.path ? 'primary.main' : 'inherit' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text} 
                sx={{ color: location.pathname === item.path ? 'primary.main' : 'inherit' }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: sidebarOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          ml: { sm: sidebarOpen ? `${drawerWidth}px` : 0 },
          transition: theme => theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {menuItems.find(item => item.path === location.pathname)?.text || 'Sistema de Inventario'}
          </Typography>
          
          {user?.location && (
            <Typography variant="body2" sx={{ mr: 2, opacity: 0.8 }}>
              {user.location.name}
            </Typography>
          )}

          
          <IconButton
            color="inherit"
            onClick={handleMenuOpen}
            sx={{ p: 0 }}
          >
            <Avatar sx={{ bgcolor: 'secondary.main' }}>
              {user?.name?.charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
          
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
          >
            <MenuItem disabled>
              <PersonIcon sx={{ mr: 1 }} />
              {user?.name}
            </MenuItem>
            <MenuItem disabled>
              <Typography variant="caption" color="text.secondary">
                {user?.role === 'ADMIN' ? 'Administrador' : 
                 user?.role === 'MANAGER' ? 'Gerente' : 'Cajero'}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <LogoutIcon sx={{ mr: 1 }} />
              Cerrar Sesión
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: sidebarOpen ? drawerWidth : 0 }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={sidebarOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="persistent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              transition: theme => theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
          open={sidebarOpen}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: sidebarOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          height: '100vh',
          overflow: 'auto',
          transition: theme => theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;